import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, keyframes } from '@chakra-ui/react';

import { useAgent } from './AgentProvider';
import Sparkle from './Sparkle';

// Floating entry to Raano AI (design-system.md §9.1): a white pill centred at the bottom on desktop,
// a dark round button above the tab bar on phones. Idle: the sparkle turns, the label shimmers and
// cycles, and a light dot travels around the pill.

const spin = keyframes`0%, 70% { transform: rotate(0) scale(1) } 80% { transform: rotate(90deg) scale(.85) } 100% { transform: rotate(180deg) scale(1) }`;
const shimmer = keyframes`from { background-position: 100% 0 } to { background-position: -100% 0 }`;
const orbit = keyframes`from { offset-distance: 0% } to { offset-distance: 100% }`;
const swap = keyframes`from { opacity: 0; transform: translateY(6px) } to { opacity: 1; transform: none }`;

const labelsFor = (pathname) => pathname.startsWith('/product/') ? ['Explore this product', 'Ask about sizing', 'Find what goes with it']
    : pathname === '/search' ? ['Shop with AI', 'Shop best sellers', 'Shop deals']
        : ['Shop with AI', 'Shop best sellers', 'Find an outfit'];

const AgentPill = () => {

    const { pathname } = useLocation();
    const { open, openAgent } = useAgent();
    const labels = labelsFor(pathname);
    const [i, setI] = useState(0);
    useEffect(() => { setI(0); }, [pathname]);
    useEffect(() => {
        const timer = setInterval(() => setI(n => (n + 1) % labels.length), 4000);
        return () => clearInterval(timer);
    }, [labels.length]);

    if (open || pathname.startsWith('/admin') || pathname === '/cart' || pathname === '/payment') return null;

    return (
        <>
            {/* Desktop pill */}
            <Box
                as='button' aria-label='Open Raano AI shopping assistant' onClick={openAgent}
                display={{ base: 'none', md: 'flex' }} position='fixed' bottom='24px' left='50%' transform='translateX(-50%)' zIndex={450}
                p='2px' borderRadius='full' boxShadow='aiPill' bg='raano.white' transition='transform .2s, box-shadow .2s'
                _hover={{ transform: 'translateX(-50%) translateY(-2px)', boxShadow: '0 6px 28px rgba(0,0,0,0.3)' }}
            >
                <Box position='relative' display='flex' alignItems='center' gap='10px' height='60px' pl='8px' pr='22px' borderRadius='full' bg='raano.white' overflow='hidden' >
                    {/* travelling light on the outline */}
                    <Box position='absolute' width='60px' height='60px' borderRadius='full' pointerEvents='none' opacity={0.5}
                        bg='radial-gradient(circle, rgba(120,140,255,0.35) 0%, transparent 70%)'
                        sx={{ offsetPath: 'inset(0 round 30px)', animation: `${orbit} 6s linear infinite`, '@media (prefers-reduced-motion: reduce)': { display: 'none' } }} />
                    <Box width='44px' height='44px' borderRadius='full' bgGradient='linear(135deg, #2E3133, #000)' color='raano.white' display='flex' alignItems='center' justifyContent='center' flexShrink={0} >
                        <Box display='flex' animation={`${spin} 5s ease-in-out infinite`} ><Sparkle size={24} /></Box>
                    </Box>
                    <Box key={labels[i]} as='span' fontFamily='ai' fontSize='16px' lineHeight='22px' whiteSpace='nowrap' sx={{ fontStretch: '100%' }}
                        color='transparent' bgClip='text' bgSize='200% 100%' bgImage='linear-gradient(90deg, #242424 0%, #242424 40%, #9A9A9A 50%, #242424 60%, #242424 100%)'
                        animation={`${swap} .3s ease-out, ${shimmer} 3s linear infinite`} >{labels[i]}</Box>
                </Box>
            </Box>

            {/* Phone button */}
            <Box
                as='button' aria-label='Open Raano AI shopping assistant' onClick={openAgent}
                display={{ base: 'flex', md: 'none' }} position='fixed' right='16px' bottom='calc(80px + env(safe-area-inset-bottom))' zIndex={450}
                width='56px' height='56px' borderRadius='full' bgGradient='linear(135deg, #2E3133, #000)' color='raano.white' boxShadow='aiPill' alignItems='center' justifyContent='center'
            >
                <Box display='flex' animation={`${spin} 5s ease-in-out infinite`} ><Sparkle size={26} /></Box>
            </Box>
        </>
    );
};

export default AgentPill;
